import { useMemo, useState } from 'react';
import { Badge, Button, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useObligationsList } from '../../../queries/obligations';
import { useProjectMembers } from '../../../queries/project-members';
import { useProjectStore } from '../../../project/project-store';
import { ObligationFormDrawer } from './ObligationFormDrawer';
import type { Obligation } from '../../../types/api';

const VALUE_TYPE_LABEL: Record<Obligation['value_type'], string> = {
  none: 'None',
  date: 'Date',
  days: 'Days',
};

export function ObligationListPage() {
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';

  const [q, setQ] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Obligation | null>(null);

  const filters = useMemo(() => ({ q: q.trim() || undefined, limit: 200 }), [q]);
  const { data: obligations = [], isLoading } = useObligationsList(filters);
  const { data: members } = useProjectMembers(projectId);

  if (!projectId) {
    return (
      <Stack>
        <Title order={2}>Obligations</Title>
        <Text c="dimmed">Pick a project to view its obligations.</Text>
      </Stack>
    );
  }

  function openCreate() {
    setEditTarget(null);
    setDrawerOpen(true);
  }

  function openEdit(o: Obligation) {
    setEditTarget(o);
    setDrawerOpen(true);
  }

  function lastUpdatedLabel(o: Obligation): string {
    const name =
      members?.find((m) => m.user_id === o.updated_by_user_id)?.username ??
      o.updated_by_user_id ??
      '—';
    return `${name} · ${new Date(o.updated_at).toLocaleDateString()}`;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Obligations</Title>
          <Text c="dimmed" size="sm">
            Project-scoped obligation catalog — attachable to leases, contracts, and wells.
          </Text>
        </Stack>
        {canWrite && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            New obligation
          </Button>
        )}
      </Group>

      <Group>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search name"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          w={320}
        />
      </Group>

      <DataTable
        withTableBorder
        highlightOnHover
        minHeight={150}
        fetching={isLoading}
        records={obligations}
        idAccessor="id"
        noRecordsText="No obligations yet"
        onRowClick={({ record }) => openEdit(record)}
        columns={[
          {
            accessor: 'name',
            title: 'Name',
            render: (o) => (
              <Text size="sm" fw={500}>
                {o.name}
              </Text>
            ),
          },
          {
            accessor: 'applies_to',
            title: 'Applies to',
            render: (o) => (
              <Group gap={4}>
                {o.applies_to_lease && (
                  <Badge variant="light" color="indigo" size="sm">
                    Lease
                  </Badge>
                )}
                {o.applies_to_contract && (
                  <Badge variant="light" color="grape" size="sm">
                    Contract
                  </Badge>
                )}
                {o.applies_to_well && (
                  <Badge variant="light" color="teal" size="sm">
                    Well
                  </Badge>
                )}
                {!o.applies_to_lease && !o.applies_to_contract && !o.applies_to_well && (
                  <Text size="xs" c="dimmed">
                    —
                  </Text>
                )}
              </Group>
            ),
          },
          {
            accessor: 'value_type',
            title: 'Value type',
            width: 120,
            render: (o) => (
              <Badge variant="outline" color="gray">
                {VALUE_TYPE_LABEL[o.value_type]}
              </Badge>
            ),
          },
          {
            accessor: 'updated_at',
            title: 'Last updated',
            render: (o) => (
              <Text size="xs" c="dimmed">
                {lastUpdatedLabel(o)}
              </Text>
            ),
          },
        ]}
      />

      <ObligationFormDrawer
        opened={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditTarget(null);
        }}
        obligation={editTarget}
      />
    </Stack>
  );
}
